/**
 * Settings → Approvals (ADR-0031) — the configurable approval engine UI.
 *
 * Two tabs:
 *   - **By module** — each gateable action lists its conditional bindings
 *     (`if <condition> → use <chain>`, first-match-wins). Add / toggle / remove
 *     rules; the condition fields come from each module's own catalog.
 *   - **Chain library** — the reusable named flows, with a live preview that
 *     resolves a chain against a scenario (server-side `previewChain`).
 *
 * @see prototype/settings/settings-approvals-v2.html — visual + behavioural baseline
 * @see docs/adr/0031-configurable-approval-chains.md
 */
import { useMemo, useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  StatusPill,
  Switch,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  cn,
  toast,
} from "@flint/ui";
import {
  Archive,
  BellOff,
  BellRing,
  Copy,
  Eye,
  GitBranch,
  Pencil,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
  Workflow,
} from "lucide-react";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell } from "../../../../features/settings/index.ts";
import { meQueryOptions } from "../../../../lib/session.ts";

export const Route = createFileRoute("/(workspace)/settings/approvals/")({
  // URL-level `approvals.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "approvals.manage")) {
      throw notFound();
    }
  },
  component: ApprovalsSettingsPage,
});

// ── helpers ───────────────────────────────────────────────────────────
const RUPEE = 100;
function fmtMoney(paise: number): string {
  return "₹" + Math.round(paise / RUPEE).toLocaleString("en-IN");
}
function conditionText(
  primary: { key: string; label: string; type: "money" | "percent" | "number" } | null,
  cond: { op: string; val?: number; extra?: Array<{ key: string; op: string; value: string | number }> },
): string {
  let head: string;
  if (!primary || cond.op === "any") head = "Always";
  else {
    const v = cond.val ?? 0;
    const shown = primary.type === "money" ? fmtMoney(v) : primary.type === "percent" ? `${v}%` : `${v}`;
    const word = cond.op === "below" ? "Below" : cond.op === "atleast" ? "At least" : "Above";
    head = `${word} ${shown}`;
  }
  if (cond.extra?.length) head += " · " + cond.extra.map((e) => `${e.key} ${e.op} ${e.value}`).join(", ");
  return head;
}
function chainFlow(stages: Array<{ rule: unknown; approvers: Array<{ type: string; value?: string }> }>): string {
  const label = (a: { type: string; value?: string }) => {
    switch (a.type) {
      case "owner": return "the Owner";
      case "manager": return "the manager";
      case "dept_head_each": return "each dept head";
      case "dept_head": return `${a.value ?? "?"} head`;
      case "role": return `any ${a.value ?? "?"}`;
      case "person": return "a specific person";
      default: return a.type;
    }
  };
  return stages
    .map((s) => s.approvers.map(label).join(s.rule === "all" ? " + " : " / "))
    .join("  →  ");
}

const selectCls =
  "h-9 rounded-md border border-line bg-paper px-2 text-sm text-ink focus:outline-none focus:border-accent";

// ── page ──────────────────────────────────────────────────────────────
function ApprovalsSettingsPage() {
  const utils = trpc.useUtils();
  const actions = trpc.approvalChains.listActions.useQuery();
  const chains = trpc.approvalChains.listChains.useQuery();
  const bindings = trpc.approvalChains.listBindings.useQuery();
  const roles = trpc.roles.list.useQuery({});
  const team = trpc.team.list.useQuery({});
  const departments = trpc.departments.list.useQuery({});

  const chainById = useMemo(() => {
    const m = new Map<string, ChainRow>();
    for (const c of chains.data?.chains ?? []) m.set(c.id, c as ChainRow);
    return m;
  }, [chains.data]);

  const upsertBinding = trpc.approvalChains.upsertBinding.useMutation({
    onSuccess: () => {
      utils.approvalChains.listBindings.invalidate();
      toast.success("Approval rule saved");
    },
    onError: (e) => toast.error(e.message),
  });
  const removeBinding = trpc.approvalChains.removeBinding.useMutation({
    onSuccess: () => {
      utils.approvalChains.listBindings.invalidate();
      toast.success("Rule removed");
    },
    onError: (e) => toast.error(e.message),
  });
  const upsertChain = trpc.approvalChains.upsertChain.useMutation({
    onSuccess: () => {
      utils.approvalChains.listChains.invalidate();
      toast.success("Approval chain saved");
    },
    onError: (e) => toast.error(e.message),
  });
  const archiveChain = trpc.approvalChains.archiveChain.useMutation({
    onSuccess: () => {
      utils.approvalChains.listChains.invalidate();
      utils.approvalChains.listBindings.invalidate();
      toast.success("Approval chain archived");
    },
    onError: (e) => toast.error(e.message),
  });

  const loading = actions.isLoading || chains.isLoading || bindings.isLoading;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Approvals" }]}>
      <SettingsShell
        activeSection="approvals"
        pageTitle="Approvals"
        pageDescription="Build reusable approval chains, then attach them to each module by condition. The first matching rule decides who signs off, in what order — over WhatsApp and in their Approvals inbox."
      >
        {loading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <TabsRoot defaultValue="modules">
            <TabsList className="mb-4">
              <TabsTrigger value="modules">By module</TabsTrigger>
              <TabsTrigger value="chains">Chain library</TabsTrigger>
            </TabsList>

            <TabsContent value="modules">
              <div className="flex flex-col gap-3">
                {(actions.data?.actions ?? []).map((action) => (
                  <ModuleCard
                    key={action.actionType}
                    action={action}
                    bindings={(bindings.data?.bindings ?? []).filter((b) => b.actionType === action.actionType)}
                    chainById={chainById}
                    chains={chains.data?.chains ?? []}
                    onSaveBinding={(input) => upsertBinding.mutate(input)}
                    onRemoveBinding={(id) => removeBinding.mutate({ id })}
                    saving={upsertBinding.isPending}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="chains">
              <ChainLibrary
                chains={chains.data?.chains ?? []}
                bindings={bindings.data?.bindings ?? []}
                roles={roles.data?.roles ?? []}
                members={team.data?.members ?? []}
                departments={departments.data?.departments ?? []}
                pickerLoading={roles.isLoading || team.isLoading || departments.isLoading}
                saving={upsertChain.isPending}
                archiving={archiveChain.isPending}
                onSave={(input) => upsertChain.mutate(input)}
                onArchive={(id) => archiveChain.mutate({ id })}
              />
            </TabsContent>
          </TabsRoot>
        )}
      </SettingsShell>
    </WorkspaceShell>
  );
}

// ── module card ───────────────────────────────────────────────────────
type ActionDescriptor = {
  actionType: string;
  label: string;
  description: string;
  primary: { key: string; label: string; type: "money" | "percent" | "number" } | null;
  fields: Array<{ key: string; label: string; type: string; options?: string[] }>;
};
type BindingRow = {
  id: string;
  actionType: string;
  priority: number;
  condition: { op: string; val?: number; extra?: Array<{ key: string; op: string; value: string | number }> };
  chainId: string;
  enabled: boolean;
};
type ApproverTargetType = "owner" | "manager" | "dept_head_each" | "dept_head" | "role" | "person";
type StageWire = {
  rule: "any" | "all" | { quorum: number };
  approvers: Array<{ type: ApproverTargetType; value?: string }>;
  notifyApprovers: boolean;
};
type ChainRow = { id: string; key: string | null; name: string; stages: StageWire[]; isSystem: boolean; usedByCount: number };
type UpsertChainInput = { id?: string; name: string; stages: StageWire[] };
type RoleOption = { id: string; key: string; name: string; description: string | null; isSystem: boolean; departmentId: string | null };
type DepartmentOption = { id: string; name: string; parentDepartmentId: string | null };
type MemberOption = {
  membershipId: string;
  userId: string;
  roleName: string | null;
  role: "Founder" | "Operator" | "Employee";
  user: { name: string; phone: string; email: string | null };
};
type ChainEditorPickers = {
  roles: RoleOption[];
  members: MemberOption[];
  departments: DepartmentOption[];
  loading: boolean;
};

function ModuleCard(props: {
  action: ActionDescriptor;
  bindings: BindingRow[];
  chainById: Map<string, ChainRow>;
  chains: ChainRow[];
  onSaveBinding: (input: {
    id?: string;
    actionType: string;
    priority: number;
    condition: { op: "any" | "above" | "atleast" | "below"; val?: number };
    chainId: string;
    enabled: boolean;
  }) => void;
  onRemoveBinding: (id: string) => void;
  saving: boolean;
}) {
  const { action, bindings, chainById, chains } = props;
  const [adding, setAdding] = useState(false);
  const sorted = [...bindings].sort((a, b) => a.priority - b.priority);

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-sm font-bold">{action.label}</h3>
            <p className="mt-0.5 text-xs text-muted">{action.description}</p>
          </div>
          <Button size="sm" intent="ghost" onClick={() => setAdding((v) => !v)}>
            {adding ? "Close" : "+ Add rule"}
          </Button>
        </div>

        {/* rules */}
        <div className="mt-3 flex flex-col gap-2">
          {sorted.length === 0 && !adding ? (
            <p className="text-xs text-muted">No approval needed for this module.</p>
          ) : null}
          {sorted.map((b) => (
            <div
              key={b.id}
              className={cn(
                "flex flex-wrap items-center gap-2 rounded-md border border-line bg-raised px-3 py-2",
                !b.enabled && "opacity-50",
              )}
            >
              <span className="text-xs font-bold uppercase tracking-wide text-muted">if</span>
              <span className="rounded border border-line bg-paper px-2 py-0.5 text-xs font-semibold text-ink">
                {conditionText(action.primary, b.condition)}
              </span>
              <span className="text-line-2">→</span>
              <span className="inline-flex items-center gap-1 rounded-full border border-accent-100 bg-accent-50 px-2.5 py-0.5 text-xs font-semibold text-accent-700">
                {chainById.get(b.chainId)?.name ?? "Unknown chain"}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs font-semibold text-accent-700 hover:underline"
                  onClick={() =>
                    props.onSaveBinding({
                      id: b.id,
                      actionType: b.actionType,
                      priority: b.priority,
                      condition: b.condition as { op: "any" | "above" | "atleast" | "below"; val?: number },
                      chainId: b.chainId,
                      enabled: !b.enabled,
                    })
                  }
                >
                  {b.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-bad hover:underline"
                  onClick={() => props.onRemoveBinding(b.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          {adding ? (
            <AddRuleForm
              action={action}
              chains={chains}
              nextPriority={sorted.length}
              saving={props.saving}
              onCancel={() => setAdding(false)}
              onSave={(input) => {
                props.onSaveBinding(input);
                setAdding(false);
              }}
            />
          ) : null}
        </div>

        {/* condition fields hint */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted">conditions:</span>
          {(action.primary ? [action.primary] : []).concat(action.fields as never).map((f) => (
            <span key={f.key} className="rounded-full border border-line bg-bg px-2 py-0.5 text-xs font-semibold text-muted">
              {f.label}
            </span>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function AddRuleForm(props: {
  action: ActionDescriptor;
  chains: ChainRow[];
  nextPriority: number;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: {
    actionType: string;
    priority: number;
    condition: { op: "any" | "above" | "atleast" | "below"; val?: number };
    chainId: string;
    enabled: boolean;
  }) => void;
}) {
  const { action, chains } = props;
  const hasPrimary = action.primary !== null;
  const [op, setOp] = useState<"any" | "above" | "atleast" | "below">(hasPrimary ? "above" : "any");
  const [amount, setAmount] = useState("");
  const [chainId, setChainId] = useState(chains[0]?.id ?? "");

  const save = () => {
    if (!chainId) return;
    let val: number | undefined;
    if (hasPrimary && op !== "any") {
      const n = parseInt(amount.replace(/[^0-9]/g, ""), 10) || 0;
      val = action.primary!.type === "money" ? n * RUPEE : n;
    }
    props.onSave({
      actionType: action.actionType,
      priority: props.nextPriority,
      condition: val !== undefined ? { op, val } : { op },
      chainId,
      enabled: true,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-line-2 bg-paper px-3 py-2">
      <span className="text-xs text-muted">When</span>
      {hasPrimary ? (
        <>
          <select className={selectCls} value={op} onChange={(e) => setOp(e.target.value as never)}>
            <option value="any">any {action.primary!.type === "percent" ? "%" : "amount"}</option>
            <option value="above">above</option>
            <option value="atleast">at least</option>
            <option value="below">below</option>
          </select>
          {op !== "any" ? (
            <input
              className="h-9 w-28 rounded-md border border-line bg-paper px-2 text-sm"
              inputMode="numeric"
              placeholder={action.primary!.type === "percent" ? "%" : "₹"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          ) : null}
        </>
      ) : (
        <span className="text-xs text-ink">it happens</span>
      )}
      <span className="text-xs text-muted">→ use</span>
      <select className={selectCls} value={chainId} onChange={(e) => setChainId(e.target.value)}>
        {chains.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <div className="ml-auto flex items-center gap-2">
        <Button size="sm" intent="ghost" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={props.saving || !chainId}>
          Add
        </Button>
      </div>
    </div>
  );
}

// ── chain library + live preview ──────────────────────────────────────
function ChainLibrary(props: {
  chains: ChainRow[];
  bindings: BindingRow[];
  roles: RoleOption[];
  members: MemberOption[];
  departments: DepartmentOption[];
  pickerLoading: boolean;
  saving: boolean;
  archiving: boolean;
  onSave: (input: UpsertChainInput) => void;
  onArchive: (id: string) => void;
}) {
  const usedBy = (chainId: string) => props.bindings.filter((b) => b.chainId === chainId).length;
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ mode: "new" | "edit" | "copy"; chain?: ChainRow } | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <Alert intent="info">
        Chains are reusable across modules — edit one here and every rule that uses it updates. Attach them to modules in the
        <strong> By module</strong> tab.
      </Alert>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-sky-50 text-sky-700">
            <GitBranch size={16} aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-ink">Chain library</h3>
            <p className="text-xs text-muted">Create reusable sign-off flows, then attach them to one or more modules.</p>
          </div>
        </div>
        <Button size="sm" className="h-11 whitespace-nowrap md:h-8" onClick={() => setEditor({ mode: "new" })}>
          <Plus size={14} aria-hidden />
          New chain
        </Button>
      </div>
      {editor ? (
        <ChainEditor
          key={`${editor.mode}-${editor.chain?.id ?? "new"}`}
          mode={editor.mode}
          saving={props.saving}
          pickers={{
            roles: props.roles,
            members: props.members,
            departments: props.departments,
            loading: props.pickerLoading,
          }}
          {...(editor.chain ? { chain: editor.chain } : {})}
          onCancel={() => setEditor(null)}
          onSave={(input) => {
            props.onSave(input);
            setEditor(null);
          }}
        />
      ) : null}
      {props.chains.map((c) => (
        <Card key={c.id} className="shadow-none">
          <CardBody>
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-start gap-3">
                <span className="inline-grid h-9 w-9 shrink-0 place-items-center rounded-sm bg-bg text-muted">
                  {c.isSystem ? <ShieldCheck size={16} aria-hidden /> : <Workflow size={16} aria-hidden />}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-bold text-ink">{c.name}</h3>
                    {c.isSystem ? (
                      <StatusPill tone="muted" size="xs">
                        Built-in
                      </StatusPill>
                    ) : null}
                    <StatusPill tone={usedBy(c.id) > 0 ? "sky" : "muted"} size="xs">
                      <Users size={11} aria-hidden />
                      {usedBy(c.id)} {usedBy(c.id) === 1 ? "rule" : "rules"}
                    </StatusPill>
                  </div>
                  <p className="mt-1 text-xs text-ink-2">{chainFlow(c.stages as never)}</p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <Button size="sm" intent="ghost" className="h-11 md:h-8" onClick={() => setPreviewId(previewId === c.id ? null : c.id)}>
                  <Eye size={14} aria-hidden />
                  {previewId === c.id ? "Hide preview" : "Preview"}
                </Button>
                {c.isSystem ? (
                  <Button size="sm" intent="ghost" className="h-11 md:h-8" onClick={() => setEditor({ mode: "copy", chain: c })}>
                    <Copy size={14} aria-hidden />
                    Copy
                  </Button>
                ) : (
                  <Button size="sm" intent="ghost" className="h-11 md:h-8" onClick={() => setEditor({ mode: "edit", chain: c })}>
                    <Pencil size={14} aria-hidden />
                    Edit
                  </Button>
                )}
                {!c.isSystem && usedBy(c.id) === 0 ? (
                  <Button size="sm" intent="ghost" className="h-11 text-bad hover:bg-bad-50 md:h-8" disabled={props.archiving} onClick={() => props.onArchive(c.id)}>
                    <Archive size={14} aria-hidden />
                    Archive
                  </Button>
                ) : null}
              </div>
            </div>
            {previewId === c.id ? <ChainPreview chainId={c.id} /> : null}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function defaultStage(): StageWire {
  return { rule: "any", approvers: [{ type: "owner" }], notifyApprovers: true };
}

function defaultValueForTarget(type: ApproverTargetType, pickers: ChainEditorPickers): string | undefined {
  switch (type) {
    case "dept_head":
      return pickers.departments[0]?.name;
    case "role":
      return pickers.roles[0]?.key;
    case "person":
      return pickers.members[0]?.userId;
    default:
      return undefined;
  }
}

function needsTargetValue(type: ApproverTargetType): boolean {
  return type === "dept_head" || type === "role" || type === "person";
}

function cleanApprover(approver: { type: ApproverTargetType; value?: string }): { type: ApproverTargetType; value?: string } {
  return {
    type: approver.type,
    ...(needsTargetValue(approver.type) && approver.value ? { value: approver.value.trim() } : {}),
  };
}

function cleanStage(stage: StageWire): StageWire {
  return {
    rule: stage.rule,
    approvers: stage.approvers.map(cleanApprover),
    notifyApprovers: stage.notifyApprovers ?? true,
  };
}

function ruleToLabel(rule: StageWire["rule"]): string {
  if (rule === "all") return "All approvers";
  if (rule === "any") return "Any one approver";
  return `${rule.quorum} approvals`;
}

function targetTypeLabel(type: ApproverTargetType): string {
  switch (type) {
    case "owner":
      return "Owner";
    case "manager":
      return "Submitter's manager";
    case "dept_head_each":
      return "Each department head";
    case "dept_head":
      return "Named department head";
    case "role":
      return "Role";
    case "person":
      return "Person";
  }
}

function approverSummary(approver: { type: ApproverTargetType; value?: string }, pickers: ChainEditorPickers): string {
  if (approver.type === "role" && approver.value) {
    return pickers.roles.find((role) => role.key === approver.value || role.name === approver.value)?.name ?? approver.value;
  }
  if (approver.type === "person" && approver.value) {
    return pickers.members.find((member) => member.userId === approver.value)?.user.name ?? "Selected member";
  }
  if (approver.type === "dept_head" && approver.value) return `${approver.value} head`;
  return targetTypeLabel(approver.type);
}

function chainEditorValidation(name: string, stages: StageWire[]): string | null {
  if (name.trim().length < 2) return "Name the chain before saving.";
  if (stages.length === 0) return "Add at least one approval stage.";
  const emptyStage = stages.findIndex((stage) => stage.approvers.length === 0);
  if (emptyStage >= 0) return `Step ${emptyStage + 1} needs at least one approver.`;
  for (let stageIndex = 0; stageIndex < stages.length; stageIndex++) {
    const stage = stages[stageIndex]!;
    for (let approverIndex = 0; approverIndex < stage.approvers.length; approverIndex++) {
      const approver = stage.approvers[approverIndex]!;
      if (needsTargetValue(approver.type) && !approver.value?.trim()) {
        return `Step ${stageIndex + 1}, approver ${approverIndex + 1}: choose a ${targetTypeLabel(approver.type).toLowerCase()}.`;
      }
    }
    if (typeof stage.rule === "object" && stage.rule.quorum > stage.approvers.length) {
      return `Step ${stageIndex + 1}: quorum cannot be higher than the number of approvers.`;
    }
  }
  return null;
}

function ChainEditor(props: {
  mode: "new" | "edit" | "copy";
  chain?: ChainRow;
  pickers: ChainEditorPickers;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: UpsertChainInput) => void;
}) {
  const [name, setName] = useState(() => {
    if (props.mode === "copy" && props.chain) return `${props.chain.name} copy`;
    return props.chain?.name ?? "";
  });
  const [stages, setStages] = useState<StageWire[]>(() =>
    props.chain?.stages?.length ? props.chain.stages.map(cleanStage) : [defaultStage()],
  );
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const updateStage = (index: number, patch: Partial<StageWire>) => {
    setStages((current) => current.map((stage, i) => (i === index ? { ...stage, ...patch } : stage)));
  };
  const updateApprover = (stageIndex: number, approverIndex: number, patch: { type?: ApproverTargetType; value?: string }) => {
    setStages((current) =>
      current.map((stage, i) =>
        i === stageIndex
          ? {
              ...stage,
              approvers: stage.approvers.map((approver, j) =>
                j === approverIndex ? cleanApprover({ ...approver, ...patch }) : approver,
              ),
            }
          : stage,
      ),
    );
  };
  const addApprover = (stageIndex: number) => {
    setStages((current) =>
      current.map((stage, i) =>
        i === stageIndex ? { ...stage, approvers: [...stage.approvers, { type: "owner" }] } : stage,
      ),
    );
  };
  const removeApprover = (stageIndex: number, approverIndex: number) => {
    setStages((current) =>
      current.map((stage, i) =>
        i === stageIndex ? { ...stage, approvers: stage.approvers.filter((_, j) => j !== approverIndex) } : stage,
      ),
    );
  };

  const validation = chainEditorValidation(name, stages);

  const save = () => {
    if (validation) {
      setError(validation);
      return;
    }
    const trimmed = name.trim();
    const cleaned = stages.map(cleanStage);
    props.onSave({
      ...(props.mode === "edit" && props.chain ? { id: props.chain.id } : {}),
      name: trimmed,
      stages: cleaned,
    });
  };

  return (
    <Card rail="sky" className="overflow-hidden shadow-none">
      <CardBody className="p-0">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line bg-raised/50 px-4 py-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-sky-50 text-sky-700">
              <Workflow size={16} aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-ink">
              {props.mode === "edit" ? "Edit chain" : props.mode === "copy" ? "Copy chain" : "New chain"}
              </h3>
              <p className="mt-0.5 text-xs text-muted">
                Build the sequence, choose real roles or people, then test who will receive the approval.
              </p>
            </div>
          </div>
          <Button size="sm" intent="ghost" className="h-11 md:h-8" onClick={props.onCancel}>
            Close
          </Button>
        </div>
        <div className="space-y-4 p-4">
          {error ? <Alert intent="bad" title="Check the chain" description={error} /> : null}
          {props.pickers.loading ? (
            <Alert intent="info" title="Loading approver lists" description="Roles, members, and departments will appear in the target pickers in a moment." />
          ) : null}
          <Field helper="Use a name your team can recognise when attaching this chain to invoices, bills, payments, or payroll.">
            <Label htmlFor="approval-chain-name">Chain name</Label>
            <Input
              id="approval-chain-name"
              value={name}
              onChange={(e) => {
                setName(e.currentTarget.value);
                setError(null);
              }}
              placeholder="Finance head then owner"
            />
          </Field>

          <div className="rounded-md border border-line bg-bg/40 px-3 py-2">
            <div className="flex flex-wrap gap-2">
              {stages.map((stage, stageIndex) => (
                <span key={stageIndex} className="inline-flex items-center gap-1 rounded-full border border-line bg-paper px-2.5 py-1 text-xs text-ink">
                  Step {stageIndex + 1}
                  <span className="text-muted">{ruleToLabel(stage.rule)}</span>
                  <span className="font-semibold">{stage.approvers.map((a) => approverSummary(a, props.pickers)).join(" / ")}</span>
                  <span className="text-muted">
                    {stage.notifyApprovers ? "notify" : "silent"}
                  </span>
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {stages.map((stage, stageIndex) => (
            <div key={stageIndex} className="overflow-hidden rounded-md border border-line bg-paper">
              <div className="grid gap-3 border-b border-line px-3 py-3 md:grid-cols-[220px_minmax(220px,1fr)_auto] md:items-start">
                <div className="flex items-center gap-2 md:min-h-16">
                  <span className="inline-grid h-7 w-7 place-items-center rounded-sm bg-sky-50 text-xs font-bold text-sky-700">
                    {stageIndex + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">Approval step</p>
                    <p className="text-xs text-muted">Runs after the previous step is satisfied.</p>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-[minmax(180px,1fr)_112px]">
                  <Field className="min-w-40 flex-1">
                    <Label htmlFor={`stage-${stageIndex}-rule`}>Rule</Label>
                    <Select
                      value={stage.rule === "all" ? "all" : stage.rule === "any" ? "any" : "quorum"}
                      onValueChange={(value) => {
                        updateStage(stageIndex, { rule: value === "quorum" ? { quorum: Math.min(2, stage.approvers.length) || 1 } : (value as "any" | "all") });
                        setError(null);
                      }}
                    >
                      <SelectTrigger id={`stage-${stageIndex}-rule`} className="text-sm md:h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any one approver</SelectItem>
                        <SelectItem value="all">All approvers</SelectItem>
                        <SelectItem value="quorum">Quorum</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {typeof stage.rule === "object" ? (
                    <Field className="w-28" {...(stage.rule.quorum > stage.approvers.length ? { error: "Too high" } : {})}>
                      <Label htmlFor={`stage-${stageIndex}-quorum`}>Needed</Label>
                      <Input
                        id={`stage-${stageIndex}-quorum`}
                        className="h-11 md:h-9"
                        inputMode="numeric"
                        value={stage.rule.quorum}
                        onChange={(e) => {
                          updateStage(stageIndex, { rule: { quorum: Math.max(1, parseInt(e.currentTarget.value, 10) || 1) } });
                          setError(null);
                        }}
                      />
                    </Field>
                  ) : null}
                </div>
                <div className="rounded-md border border-line bg-bg/50 p-2 md:col-start-2 md:row-start-2">
                  <ToggleInline
                    checked={stage.notifyApprovers ?? true}
                    title="Notify active approvers"
                    description={
                      stage.notifyApprovers ?? true
                        ? "Send a WhatsApp push when this step becomes active."
                        : "Keep this step in the approvals inbox without sending a push."
                    }
                    onChange={(checked) => {
                      updateStage(stageIndex, { notifyApprovers: checked });
                      setError(null);
                    }}
                  />
                </div>
                <div className="flex md:justify-end">
                  <Button
                    size="sm"
                    intent="ghost"
                    className="h-11 text-bad hover:bg-bad-50 md:h-9"
                    disabled={stages.length === 1}
                    onClick={() => setStages((current) => current.filter((_, i) => i !== stageIndex))}
                    title={stages.length === 1 ? "A chain needs at least one step" : "Remove step"}
                  >
                    <Trash2 size={14} aria-hidden />
                    Remove
                  </Button>
                </div>
              </div>
              <div className="flex flex-col gap-2 p-3">
                {stage.approvers.map((approver, approverIndex) => (
                  <ApproverTargetRow
                    key={approverIndex}
                    approver={approver}
                    pickers={props.pickers}
                    stageIndex={stageIndex}
                    approverIndex={approverIndex}
                    canRemove={stage.approvers.length > 1}
                    onChange={(patch) => updateApprover(stageIndex, approverIndex, patch)}
                    onRemove={() => removeApprover(stageIndex, approverIndex)}
                  />
                ))}
                <div>
                  <Button size="sm" intent="ghost" className="h-11 border border-line bg-paper md:h-8" onClick={() => addApprover(stageIndex)}>
                    <Plus size={14} aria-hidden />
                    Add approver
                  </Button>
                </div>
              </div>
            </div>
            ))}
          </div>
          {showPreview ? <ChainDraftPreview stages={stages.map(cleanStage)} pickers={props.pickers} /> : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-raised/40 px-4 py-3">
          <Button size="sm" intent="ghost" className="h-11 border border-line bg-paper md:h-8" onClick={() => setStages((current) => [...current, defaultStage()])}>
            <Plus size={14} aria-hidden />
            Add stage
          </Button>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            {validation ? <p className="text-xs text-muted">{validation}</p> : null}
            <Button size="sm" intent="ghost" className="h-11 md:h-8" onClick={() => setShowPreview((value) => !value)} disabled={!!validation}>
              <Eye size={14} aria-hidden />
              {showPreview ? "Hide test" : "Test chain"}
            </Button>
            <Button size="sm" intent="ghost" className="h-11 md:h-8" onClick={props.onCancel}>
              Cancel
            </Button>
            <Button size="sm" className="h-11 md:h-8" disabled={props.saving || !!validation} onClick={save} title={validation ?? "Save chain"}>
              <Save size={14} aria-hidden />
              {props.saving ? "Saving..." : "Save chain"}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function ApproverTargetRow(props: {
  approver: { type: ApproverTargetType; value?: string };
  pickers: ChainEditorPickers;
  stageIndex: number;
  approverIndex: number;
  canRemove: boolean;
  onChange: (patch: { type?: ApproverTargetType; value?: string }) => void;
  onRemove: () => void;
}) {
  const pickerId = `stage-${props.stageIndex}-approver-${props.approverIndex}`;
  const targetHelp =
    props.approver.type === "manager"
      ? "Uses the submitter's reporting manager from Team settings."
      : props.approver.type === "dept_head_each"
        ? "Fans out using the departments on the submitted document."
        : props.approver.type === "owner"
          ? "Resolves to Founder or wildcard permission holders."
          : undefined;

  return (
    <div className="rounded-md border border-line bg-bg/40 p-3">
      <div className="grid gap-3 md:grid-cols-[220px_minmax(220px,1fr)_auto] md:items-start">
        <Field helper="Who should approve at this step.">
          <Label htmlFor={`${pickerId}-type`}>Approver target</Label>
          <Select
            value={props.approver.type}
            onValueChange={(value) => {
              const type = value as ApproverTargetType;
              const targetValue = defaultValueForTarget(type, props.pickers);
              props.onChange({ type, ...(targetValue ? { value: targetValue } : {}) });
            }}
          >
            <SelectTrigger id={`${pickerId}-type`} className="text-sm md:h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="manager">Submitter's manager</SelectItem>
              <SelectItem value="dept_head_each">Each department head</SelectItem>
              <SelectItem value="dept_head">Named department head</SelectItem>
              <SelectItem value="role">Role</SelectItem>
              <SelectItem value="person">Person</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <TargetValueField
          id={`${pickerId}-value`}
          approver={props.approver}
          pickers={props.pickers}
          {...(targetHelp ? { helper: targetHelp } : {})}
          onChange={(value) => props.onChange({ value })}
        />

        <div className="flex md:justify-end">
          <Button
            size="sm"
            intent="ghost"
            className="mt-6 h-11 text-bad hover:bg-bad-50 md:h-9"
            disabled={!props.canRemove}
            onClick={props.onRemove}
            title={props.canRemove ? "Remove approver" : "A step needs at least one approver"}
          >
            <Trash2 size={14} aria-hidden />
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
}

function ToggleInline(props: {
  checked: boolean;
  title: string;
  description: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        <span
          className={cn(
            "mt-0.5 inline-grid h-7 w-7 shrink-0 place-items-center rounded-sm",
            props.checked ? "bg-sky-50 text-sky-700" : "bg-bg text-muted",
          )}
        >
          {props.checked ? (
            <BellRing size={14} aria-hidden />
          ) : (
            <BellOff size={14} aria-hidden />
          )}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">{props.title}</p>
          <p className="text-xs leading-relaxed text-muted">{props.description}</p>
        </div>
      </div>
      <Switch
        checked={props.checked}
        onCheckedChange={props.onChange}
        aria-label={props.title}
      />
    </div>
  );
}

function TargetValueField(props: {
  id: string;
  approver: { type: ApproverTargetType; value?: string };
  pickers: ChainEditorPickers;
  helper?: string;
  onChange: (value: string) => void;
}) {
  if (props.approver.type === "role") {
    return (
      <Field helper={props.pickers.roles.length ? "Saved as the selected role so this chain stays stable." : "Create roles in Settings > Roles & org."}>
        <Label htmlFor={props.id}>Role</Label>
        <Select {...(props.approver.value ? { value: props.approver.value } : {})} onValueChange={props.onChange} disabled={props.pickers.roles.length === 0}>
          <SelectTrigger id={props.id} className="text-sm md:h-9">
            <SelectValue placeholder="Choose role" />
          </SelectTrigger>
          <SelectContent>
            {props.pickers.roles.map((role) => (
              <SelectItem key={role.id} value={role.key}>
                {role.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    );
  }
  if (props.approver.type === "person") {
    return (
      <Field helper={props.pickers.members.length ? "Pick an active workspace member from Team settings." : "Invite members in Settings > Team & roles."}>
        <Label htmlFor={props.id}>Member</Label>
        <Select {...(props.approver.value ? { value: props.approver.value } : {})} onValueChange={props.onChange} disabled={props.pickers.members.length === 0}>
          <SelectTrigger id={props.id} className="text-sm md:h-9">
            <SelectValue placeholder="Choose member" />
          </SelectTrigger>
          <SelectContent>
            {props.pickers.members.map((member) => (
              <SelectItem key={member.membershipId} value={member.userId}>
                {member.user.name} · {member.roleName ?? member.role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    );
  }
  if (props.approver.type === "dept_head") {
    return (
      <Field helper={props.pickers.departments.length ? "Resolves to the head of this department." : "Create departments in Settings > Roles & org."}>
        <Label htmlFor={props.id}>Department</Label>
        <Select {...(props.approver.value ? { value: props.approver.value } : {})} onValueChange={props.onChange} disabled={props.pickers.departments.length === 0}>
          <SelectTrigger id={props.id} className="text-sm md:h-9">
            <SelectValue placeholder="Choose department" />
          </SelectTrigger>
          <SelectContent>
            {props.pickers.departments.map((department) => (
              <SelectItem key={department.id} value={department.name}>
                {department.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    );
  }
  return (
    <Field helper={props.helper ?? "No extra value is needed for this target."}>
      <Label>Resolution</Label>
      <div className="flex min-h-11 items-center rounded-sm border border-line bg-paper px-3 py-2 md:min-h-9">
        <p className="text-sm font-semibold text-ink">{approverSummary(props.approver, props.pickers)}</p>
      </div>
    </Field>
  );
}

function ChainDraftPreview(props: { stages: StageWire[]; pickers: ChainEditorPickers }) {
  const [amount, setAmount] = useState("600000");
  const [departments, setDepartments] = useState<string[]>(() => props.pickers.departments.slice(0, 2).map((d) => d.name));
  const preview = trpc.approvalChains.previewChain.useQuery({
    stages: props.stages,
    actionType: "purchase_order",
    scenario: { primary: parseInt(amount.replace(/[^0-9]/g, ""), 10) || 0, departments, fields: {} },
  });

  return (
    <div className="rounded-md border border-sky-100 bg-sky-50/30 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-ink">Test this chain</h4>
          <p className="text-xs text-muted">Preview uses a purchase order scenario and resolves the draft without saving it.</p>
        </div>
        <StatusPill tone={preview.data?.stages.length ? "ok" : "muted"} size="xs" dot>
          {preview.isLoading ? "Resolving" : preview.data?.stages.length ? "Resolved" : "No approvers"}
        </StatusPill>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-[160px_1fr]">
        <Field helper="Whole rupees for the test scenario.">
          <Label htmlFor="draft-preview-amount">PO amount</Label>
          <Input id="draft-preview-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.currentTarget.value)} />
        </Field>
        <Field helper="Used by each-department-head targets.">
          <Label>Departments involved</Label>
          <div className="flex flex-wrap gap-2">
            {props.pickers.departments.map((department) => {
              const selected = departments.includes(department.name);
              return (
                <button
                  key={department.id}
                  type="button"
                  className={cn(
                    "min-h-11 rounded-full border px-3 py-1 text-xs font-semibold transition md:min-h-0 md:px-2.5",
                    selected ? "border-sky-200 bg-sky-50 text-sky-700" : "border-line bg-paper text-muted hover:text-ink",
                  )}
                  onClick={() =>
                    setDepartments((current) =>
                      selected ? current.filter((name) => name !== department.name) : [...current, department.name],
                    )
                  }
                >
                  {department.name}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <Separator className="my-3" />
      {preview.error ? (
        <Alert intent="bad" title="Preview failed" description={preview.error.message} />
      ) : preview.isLoading ? (
        <p className="text-xs text-muted">Resolving approvers...</p>
      ) : preview.data && preview.data.stages.length > 0 ? (
        <ol className="space-y-2">
          {preview.data.stages.map((stage) => (
            <li key={stage.order} className="rounded-sm border border-line bg-paper px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-muted">Step {stage.order + 1}</span>
                <span className="text-xs text-muted">{ruleToLabel(stage.rule)}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {stage.approvers.map((approver) => (
                  <span key={approver.userId} className="inline-flex items-center gap-1 rounded-full border border-line bg-bg px-2.5 py-1 text-xs font-semibold text-ink">
                    <UserRound size={12} aria-hidden />
                    {approver.name}
                    <span className="font-normal text-muted">{approver.resolvedFrom}</span>
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-xs text-muted">No approvers resolved. In a live request, Flint falls back to owners instead of auto-approving.</p>
      )}
    </div>
  );
}

function ChainPreview(props: { chainId: string }) {
  const [amount, setAmount] = useState("600000");
  const [deptsCsv, setDeptsCsv] = useState("Production, Procurement");
  const departments = deptsCsv.split(",").map((s) => s.trim()).filter(Boolean);

  const preview = trpc.approvalChains.previewChain.useQuery({
    chainId: props.chainId,
    actionType: "purchase_order",
    scenario: { primary: parseInt(amount.replace(/[^0-9]/g, ""), 10) || 0, departments, fields: {} },
  });

  return (
    <div className="mt-3 rounded-md border border-accent-100 bg-accent-50/40 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-muted">
          Amount (₹)
          <input
            className="mt-1 block h-9 w-32 rounded-md border border-line bg-paper px-2 text-sm"
            value={amount}
            inputMode="numeric"
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label className="flex-1 text-xs text-muted">
          Departments involved (comma-separated)
          <input
            className="mt-1 block h-9 w-full rounded-md border border-line bg-paper px-2 text-sm"
            value={deptsCsv}
            onChange={(e) => setDeptsCsv(e.target.value)}
          />
        </label>
      </div>
      <div className="mt-3">
        {preview.isLoading ? (
          <p className="text-xs text-muted">Resolving…</p>
        ) : preview.data && preview.data.stages.length > 0 ? (
          <ol className="flex flex-col gap-2">
            {preview.data.stages.map((s) => (
              <li key={s.order} className="flex flex-col gap-1">
                <span className="text-xs font-bold uppercase tracking-wide text-muted">
                  Step {s.order + 1} · {s.rule === "all" ? "ALL must approve" : "ANY ONE approves"}
                </span>
                <div className="flex flex-wrap gap-2">
                  {s.approvers.map((a) => (
                    <span key={a.userId} className="rounded-full border border-line bg-paper px-2.5 py-1 text-xs font-semibold text-ink">
                      {a.name}
                      <span className="ml-1 text-xs font-normal text-muted">{a.resolvedFrom}</span>
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-xs text-muted">No approvers resolved for this scenario.</p>
        )}
      </div>
    </div>
  );
}
